import { Models } from "@hex-insights/app-modules";
import { Button, Column, Grid, If, makeClassName, Row, TruncatedText } from "@hex-insights/core";
import {
	BooleanField,
	CheckBoxInput,
	DateTimeField,
	FileUploadField,
	FormType,
	Label,
	RadioField,
	TextAreaInput,
	TextField,
	TextFieldInputProps,
	useFieldState,
} from "@hex-insights/forms";
import { Icon } from "../../../Components/Icon";
import { User, UserExternalNotificationFrequency, UserPublicNameFormat } from "../../../Utilities";
import { UserIcon } from "../../Components/User/Icon";
import { UserFormat } from "../../Format";
import { UserFormValidation, UserFormValues } from "../../FormUtilities";
import { UserUtilities } from "../../Utilities";
import { NameOrderField } from "../_General";
import { PermissionGroupSelectField } from "../PermissionGroup";
import { BaseFieldProps } from "../shared";
import styles from "./field.module.css";

/**
 * Generic props for fields of the User model.
 */
type FieldProps<K extends keyof UserFormValues.Base = keyof UserFormValues.Base> = BaseFieldProps<
	Pick<UserFormValues.Base, K>
>;

/**
 * Generic props for fields of the User model that only appear in the detail form.
 */
type DetailFieldProps<K extends keyof UserFormValues.Detail = keyof UserFormValues.Detail> = BaseFieldProps<
	Pick<UserFormValues.Detail, K>
>;

/**
 * Field component for the `image` field of the User model.
 */
export function Image({ formState, url, ...props }: FieldProps<"imageKey" | "imageContentType"> & { url?: string }) {
	Models.requireURLForUpdateForm(url, false, formState.formType, "Image");

	return (
		<FileUploadField
			formState={formState}
			keyFieldName="imageKey"
			contentTypeFieldName="imageContentType"
			url={url}
			optional
			{...props}
		/>
	);
}

/**
 * Field component for the `name` field of the User model.
 */
export function Name({ formState, ...props }: DetailFieldProps<"name">) {
	return <TextField formState={formState} name="name" {...props} />;
}

/**
 * Field component for the `publicName` field of the User model.
 */
export function PublicName({ formState, ...props }: DetailFieldProps<"publicName">) {
	return <TextField formState={formState} name="publicName" {...props} />;
}

/**
 * Field component for the `givenName` field of the User model.
 */
export function GivenName({ formState, ...props }: FieldProps<"givenName">) {
	return <TextField formState={formState} name="givenName" optional {...props} />;
}

/**
 * Field component for the `middleName` field of the User model.
 */
export function MiddleName({ formState, ...props }: FieldProps<"middleName">) {
	return <TextField formState={formState} name="middleName" optional {...props} />;
}

/**
 * Field component for the `familyName` field of the User model.
 */
export function FamilyName({ formState, ...props }: FieldProps<"familyName">) {
	return <TextField formState={formState} name="familyName" optional {...props} />;
}

/**
 * Field component for the `nameOrder` field of the User model.
 */
export function NameOrder({ formState, ...props }: FieldProps<"nameOrder" | "givenName" | "familyName">) {
	const { givenName, familyName } = formState.formValues;

	return (
		<NameOrderField formState={formState} {...props} name="nameOrder" givenName={givenName} familyName={familyName} />
	);
}

/**
 * Field component for the `publicNameFormat` field of the User model.
 */
export function PublicNameFormat({
	formState,
}: FieldProps<"publicNameFormat" | "givenName" | "familyName" | "nameOrder"> & {
	user?: Pick<User, "givenName" | "familyName" | "nameOrder">;
}) {
	const fieldState = useFieldState({ formState, name: "publicNameFormat" });

	const previewUser = {
		...UserUtilities.placeholderUser,
		name: UserFormat.nameFromFields(formState.formValues),
		givenName: formState.formValues.givenName,
		familyName: formState.formValues.familyName,
		nameOrder: formState.formValues.nameOrder,
	};

	return (
		<Column justify="spaced-start" verticalSpacing="1.5rem">
			<Grid tracks="auto-fill" minTrackWidth="12.5rem" gap="1.5rem">
				<Button
					onClick={() => fieldState.setValue(UserPublicNameFormat.FullName)}
					className={makeClassName(
						styles["public-name-format-button"],
						fieldState.value === UserPublicNameFormat.FullName ? styles["public-name-format-button--selected"] : "",
					)}
				>
					<Row justify="spaced-start" align="center">
						<UserIcon user={previewUser} size={32} />
						<TruncatedText>
							{UserFormat.publicNameFromFields({
								...previewUser,
								publicNameFormat: UserPublicNameFormat.FullName,
							})}
						</TruncatedText>
					</Row>
				</Button>
				<Button
					onClick={() => fieldState.setValue(UserPublicNameFormat.GivenNameFamilyInitial)}
					className={makeClassName(
						styles["public-name-format-button"],
						fieldState.value === UserPublicNameFormat.GivenNameFamilyInitial
							? styles["public-name-format-button--selected"]
							: "",
					)}
				>
					<Row justify="spaced-start" align="center">
						<UserIcon user={previewUser} size={32} />
						<span>
							{UserFormat.publicNameFromFields({
								...previewUser,
								publicNameFormat: UserPublicNameFormat.GivenNameFamilyInitial,
							})}
						</span>
					</Row>
				</Button>
			</Grid>

			<Column justify="spaced-start" verticalSpacing="1rem">
				<Row justify="spaced-start" align="center" horizontalSpacing="0.5rem">
					<CheckBoxInput
						id="public-name-format-hidden"
						value={fieldState.value === UserPublicNameFormat.Hidden}
						setValue={(v) => fieldState.setValue(v ? UserPublicNameFormat.Hidden : UserPublicNameFormat.FullName)}
						style={{ marginTop: "0.125rem" }}
					/>

					<Label htmlFor="public-name-format-hidden">I do not want my name displayed in the SEE Learning Portal.</Label>
				</Row>

				<If condition={formState.formValues.publicNameFormat === UserPublicNameFormat.Hidden}>
					<Row justify="spaced-start" align="center" className={styles["warning-notice"]}>
						<Icon.WarningOctagon color="var(--theme-colors__pink)" size={20} />
						Please note that by choosing to hide your name in the SEE Learning Portal you will not be able to comment on
						posts, respond in group messages, and may be limited in other ways.
					</Row>
				</If>
			</Column>
		</Column>
	);
}

/**
 * Field component for the `organizationName` field of the User model.
 */
export function OrganizationName({ formState, ...props }: FieldProps<"organizationName">) {
	return <TextField formState={formState} name="organizationName" optional {...props} />;
}

/**
 * Field component for the `gender` field of the User model.
 */
export function Gender({ formState, ...props }: FieldProps<"gender">) {
	return <TextField formState={formState} name="gender" optional {...props} />;
}

/**
 * Field component for the `pronouns` field of the User model.
 */
export function Pronouns({ formState, ...props }: FieldProps<"pronouns">) {
	return <TextField formState={formState} name="pronouns" optional {...props} />;
}

/**
 * Field component for the `dateOfBirth` field of the User model.
 */
export function DateOfBirth({ formState, ...props }: FieldProps<"dateOfBirth">) {
	return <DateTimeField formState={formState} name="dateOfBirth" optional precision="day" {...props} />;
}

/**
 * Field component for the `description` field of the User model.
 */
export function Description({ formState, label = "Bio", ...props }: FieldProps<"description">) {
	return (
		<TextField
			formState={formState}
			name="description"
			label={label}
			optional
			multiLine
			Input={DescriptionInput}
			{...props}
		/>
	);
}

function DescriptionInput(props: TextFieldInputProps) {
	return <TextAreaInput {...props} minRows={3} />;
}

/**
 * Field component for the `email` field of the User model.
 */
export function Email({ formState, instanceID, ...props }: FieldProps<"email"> & Partial<Models.FieldInstanceIDProps>) {
	Models.requireInstanceIDForUpdateForm(instanceID, formState.formType, "Email");
	const validateUnique = UserFormValidation.useUniqueEmailValidator(instanceID);

	return <TextField formState={formState} name="email" scheduledValidation={validateUnique} {...props} />;
}

/**
 * Field component for the `status` field of the User model.
 */
export function Status({ formState, ...props }: DetailFieldProps<"status">) {
	return (
		<RadioField
			formState={formState}
			name="status"
			options={UserFormValues.statusOptions}
			blankValue={null}
			optional={FormType.isCreate(formState.formType)}
			{...props}
		/>
	);
}

/**
 * Field component for the `passwordUpdatedAt` field of the User model.
 */
export function PasswordUpdatedAt({ formState, ...props }: DetailFieldProps<"passwordUpdatedAt">) {
	return (
		<DateTimeField
			formState={formState}
			name="passwordUpdatedAt"
			optional={FormType.isCreate(formState.formType)}
			precision="minute"
			{...props}
		/>
	);
}

/**
 * Field component for the `isCurrent` field of the User model.
 */
export function IsCurrent({ formState, ...props }: DetailFieldProps<"isCurrent">) {
	return <BooleanField formState={formState} name="isCurrent" {...props} />;
}

/**
 * Field component for the `timeZoneName` field of the User model.
 */
export function TimeZoneName({ formState, ...props }: FieldProps<"timeZoneName">) {
	return <TextField formState={formState} name="timeZoneName" optional {...props} />;
}

const externalNotificationFrequencyResponsiveTracks = {
	0: 1,
	500: 3,
};

/**
 * Field component for the `externalNotificationFrequency` field of the User model.
 */
export function ExternalNotificationFrequency({ formState }: DetailFieldProps<"externalNotificationFrequency">) {
	const fieldState = useFieldState({ formState, name: "externalNotificationFrequency" });

	return (
		<Grid responsiveTracks={externalNotificationFrequencyResponsiveTracks} gap="0.75rem">
			<Button
				onClick={() => fieldState.setValue(UserExternalNotificationFrequency.Daily)}
				className={makeClassName(
					styles["ext-notif-freq-option-button"],
					fieldState.value === UserExternalNotificationFrequency.Daily
						? styles["ext-notif-freq-option-button--selected"]
						: "",
				)}
				style={{ width: "100%" }}
			>
				Once a Day
			</Button>
			<Button
				onClick={() => fieldState.setValue(UserExternalNotificationFrequency.Weekly)}
				className={makeClassName(
					styles["ext-notif-freq-option-button"],
					fieldState.value === UserExternalNotificationFrequency.Weekly
						? styles["ext-notif-freq-option-button--selected"]
						: "",
				)}
				style={{ width: "100%" }}
			>
				Once a Week
			</Button>
			<Button
				onClick={() => fieldState.setValue(UserExternalNotificationFrequency.Never)}
				className={makeClassName(
					styles["ext-notif-freq-option-button"],
					fieldState.value === UserExternalNotificationFrequency.Never
						? styles["ext-notif-freq-option-button--selected"]
						: "",
				)}
				style={{ width: "100%" }}
			>
				When Necessary
			</Button>
		</Grid>
	);
}

export namespace PermissionGroups {
	export type SelectProps = FieldProps<"permissionGroupIDs"> &
		Omit<PermissionGroupSelectField.MultiSelectProps<UserFormValues.Base>, "formState" | "name" | "optional">;

	/**
	 * Field component for the `permissionGroups` edge of the User model.
	 */
	export function Select({ formState, ...props }: SelectProps) {
		return <PermissionGroupSelectField.MultiSelect formState={formState} name="permissionGroupIDs" {...props} />;
	}
}
